import {
  Box,
  Container,
  Spinner,
  Stack,
  Text,
  Heading,
  Grid,
  Badge,
  IconButton,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { GET } from '../utilities/ApiProvider';
import { baseUrl } from '../utilities/customUrls';

export default function VideoAlbumDetail() {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { albumId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const albumName = location.state?.albumName || 'Video Album';

  const getYoutubeEmbedUrl = youtubeUrl => {
    if (!youtubeUrl) return '';

    let videoId = '';

    if (youtubeUrl.includes('youtube.com/watch?v=')) {
      videoId = youtubeUrl.split('v=')[1]?.split('&')[0];
    } else if (youtubeUrl.includes('youtu.be/')) {
      videoId = youtubeUrl.split('youtu.be/')[1]?.split('?')[0];
    } else if (youtubeUrl.includes('youtube.com/embed/')) {
      videoId = youtubeUrl.split('embed/')[1]?.split('?')[0];
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };

  const getVideos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await GET(`${baseUrl}/VideoAlbum/GetAlbumVideos/${albumId}`, {
        authorization: `Basic ${btoa('admin:admin123')}`,
      });

      if (response?.Data && Array.isArray(response.Data)) {
        setVideos(response.Data);
      } else {
        setVideos([]);
        setError('No videos found in this album');
      }
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError('Failed to load videos. Please try again later.');
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getVideos();
  }, [albumId]);

  return (
    <Stack py="80px" minH="100vh">
      <Container maxW="6xl">
        <Stack direction="row" align="center" mb={8} spacing={4}>
          <IconButton
            icon={<ArrowBackIcon />}
            aria-label="Go back"
            onClick={() => navigate('/media/video-gallery')}
            variant="ghost"
            size="lg"
          />
          <Heading size="lg">{albumName}</Heading>
        </Stack>

        {error && (
          <Text color="red.500" textAlign="center" mb={4}>
            {error}
          </Text>
        )}

        {isLoading ? (
          <Stack align="center" justify="center" minH="300px">
            <Spinner size="xl" />
          </Stack>
        ) : videos.length === 0 ? (
          <Stack align="center" justify="center" minH="300px">
            <Text fontSize="lg" color="gray.500">
              No videos in this album
            </Text>
          </Stack>
        ) : (
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(2, 1fr)',
            }}
            gap={8}
          >
            {videos.map(video => (
              <Box
                key={video.Id}
                borderWidth="1px"
                borderRadius="lg"
                overflow="hidden"
                boxShadow="md"
                transition="all 0.3s"
                _hover={{ boxShadow: 'xl' }}
              >
                <Box position="relative" paddingBottom="56.25%" h={0}>
                  <iframe
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                    src={getYoutubeEmbedUrl(video.YoutubeUrl)}
                    title={video.Title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Box>

                <Box p={4}>
                  <Heading size="sm" mb={2} noOfLines={2}>
                    {video.Title}
                  </Heading>
                  {video.Description && (
                    <Text fontSize="sm" color="gray.600" noOfLines={3} mb={2}>
                      {video.Description}
                    </Text>
                  )}
                  {video.Duration && (
                    <Badge colorScheme="green">{video.Duration}</Badge>
                  )}
                </Box>
              </Box>
            ))}
          </Grid>
        )}
      </Container>
    </Stack>
  );
}
